import { handleUnknownError } from '@news-mono/common'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
    AllEvents,
    AppState,
    authCheckAccessToken,
    AuthenticationState,
    ConfigurationContext,
    DataLayerEventName,
    ExtendedAccessEvent,
} from '@news-mono/web-common'
import { IconSpinner } from '../../../../icons'
import { usePromptClickHandler } from '../../../../__helpers/prompt-click-helper'
import { TheWestMiniIcon } from './assets/TheWestMiniIcon'
import { TheWestMiniStarIcon } from './assets/TheWestMiniStarIcon'
import { TheWestStarIcon } from './assets/TheWestStarIcon'
import {
    StyledContinueButton,
    StyledPurchaseCompleteHeader,
    StyledPurchaseCompletePrompt,
    StyledPurchaseCompletePromptContainer,
    StyledPurchaseCompleteText,
    StyledTextContainer,
} from './PurchaseCompletePrompt.styled'
import { useDispatch, useSelector } from 'react-redux'
import { LoggerContext } from '../../../../diagnostics/LoggerContext'
import debug from 'debug'
import {
    BeginLoadingEvent,
    EndLoadingEvent,
} from '@west-australian-newspapers/react-ssr-data-loader/dist/cjs/events'
import { colors } from '../../../../__styling'

export const PURCHASE_COMPLETE_PROMPT_ID = 'purchase-complete-prompt'
const purchaseCompleteDebug = debug('purchase-complete-prompt')

export interface PurchaseCompletePromptProps {
    onEvent: (
        event:
            | AllEvents
            | BeginLoadingEvent
            | EndLoadingEvent
            | ExtendedAccessEvent,
    ) => void
}

export const SwgPurchaseCompletePrompt = ({
    onEvent,
}: PurchaseCompletePromptProps) => {
    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    // State to check for entitlements
    const [isCheckingForEntitlements, setIsCheckingForEntitlements] =
        useState(true)
    // Check if the tab is currently in view
    const [isTabInView, setTabInView] = useState(true)

    const config = useContext(ConfigurationContext)
    const logger = useContext(LoggerContext)
    const dispatch = useDispatch()

    const isLoggedIn = useCallback(
        () => authentication.isLoggedIn,
        [authentication],
    )
    const hasEntitlementsHandshaked = useCallback(
        () => authentication.entitlements.includes('thewest:all'),
        [authentication],
    )

    // Handle the prompt display hiding/showing
    const { isHidden, backgroundComponentRef, hide } = usePromptClickHandler({
        promptId: PURCHASE_COMPLETE_PROMPT_ID,
        shouldReadFromLocalStorage: true,
        disableOutsideClick: isCheckingForEntitlements,
        debug: purchaseCompleteDebug.extend('handler'),
    })

    useEffect(() => {
        // If we are not checking for entitlements, the prompt is hidden, or the tab is not in view, do nothing
        if (!isCheckingForEntitlements || isHidden || !isTabInView) {
            return
        }
        if (!isLoggedIn()) {
            purchaseCompleteDebug(
                'User is not logged in, stopping entitlement checks until further notice.',
            )
            return
        }

        const hasEntitlements = hasEntitlementsHandshaked()

        purchaseCompleteDebug('Checking for entitlements: ', {
            hasEntitlements,
        })
        setIsCheckingForEntitlements(!hasEntitlements)

        if (hasEntitlements) {
            return
        }

        // Check again in 5 seconds
        const intervalCheck = setInterval(async () => {
            try {
                purchaseCompleteDebug('Re-checking for entitlements...')
                await dispatch(
                    authCheckAccessToken({
                        onEvent,
                        log: logger,
                        config,
                        invocation: 'auto',
                        forceRefresh: true,
                        onInvocationEvent: onEvent,
                    }),
                )
            } catch (error) {
                const err = handleUnknownError(error)
                logger.error(
                    { err },
                    'Error while checking for entitlements after purchase completion',
                )
            }
        }, 5000)

        return () => clearInterval(intervalCheck)
    }, [
        hasEntitlementsHandshaked,
        isLoggedIn,
        config,
        dispatch,
        isCheckingForEntitlements,
        logger,
        onEvent,
        isHidden,
        isTabInView,
    ])

    // Listen for visibility changes to the document
    useEffect(() => {
        const handleVisibilityChange = () => {
            const isDocumentHidden = document.hidden
            setTabInView(!isDocumentHidden)
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            )
        }
    }, [])

    // Listen for document render and send event
    useEffect(() => {
        if (!isHidden && isLoggedIn()) {
            onEvent({
                type: DataLayerEventName.subscribeWithGoogle,
                originator: 'SwGPurchaseCompletePrompt',
                payload: {
                    eventType: 'SUBSCRIPTION_COMPLETED_IMPRESSION',
                },
            })
        }
        // We only want to run this once on mount for the prompt
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHidden, isLoggedIn])

    return isHidden || !isLoggedIn() ? null : (
        <StyledPurchaseCompletePrompt ref={backgroundComponentRef}>
            <StyledPurchaseCompletePromptContainer
                loading={isCheckingForEntitlements}
                aria-label={'Subscription Purchase Complete'}
                aria-busy={isCheckingForEntitlements}
                aria-modal={true}
                role="dialog"
            >
                {isCheckingForEntitlements ? (
                    <>
                        <IconSpinner
                            height={48}
                            width={48}
                            color={colors.black}
                        />
                        <StyledTextContainer loading={true}>
                            <StyledPurchaseCompleteHeader loading={true}>
                                We're still adding your subscription
                            </StyledPurchaseCompleteHeader>
                            <StyledPurchaseCompleteText loading={true}>
                                It normally only takes a moment, but sometimes
                                it can take a few minutes.
                            </StyledPurchaseCompleteText>
                            <StyledPurchaseCompleteText loading={true}>
                                If you need help, call 1800 811 855 (Mon-Fri
                                7am-4pm, Sat-Sun 7am-noon).
                            </StyledPurchaseCompleteText>
                        </StyledTextContainer>
                    </>
                ) : (
                    <>
                        <TheWestStarIcon />
                        <TheWestMiniStarIcon index={1} />
                        <TheWestMiniStarIcon index={2} />
                        <StyledTextContainer loading={false}>
                            <TheWestMiniIcon />
                            <StyledPurchaseCompleteHeader loading={false}>
                                Subscription purchase completed
                            </StyledPurchaseCompleteHeader>
                            <StyledPurchaseCompleteText loading={false}>
                                Thanks for subscribing with Google.
                            </StyledPurchaseCompleteText>
                        </StyledTextContainer>
                        <StyledContinueButton onClick={hide}>
                            Continue
                        </StyledContinueButton>
                    </>
                )}
            </StyledPurchaseCompletePromptContainer>
        </StyledPurchaseCompletePrompt>
    )
}
